import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Button,

} from '@mui/material';
import { AccessTime, Visibility, Group, Lightbulb, LockOutlined } from '@mui/icons-material';
import { useAuth } from '../context/AuthProvider.tsx';

// Original AboutPage component unchanged
const AboutPageContent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, login } = useAuth();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const [scrolled, setScrolled] = useState(false);

  // Add scroll event listener to detect when page is scrolled
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Initialize on mount
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: "#0A0F15", color: "white", position: 'relative', overflow: 'hidden', scrollBehavior: "smooth" }}>

      {/* Top Banner */}
      {!user &&
        <AppBar position="fixed" sx={{ background: scrolled ? "#0A0F15" : "none", boxShadow: 'none', borderBottom: !scrolled ? 'none' : '1px solid #3b3b3b', zIndex: 40 }}>
          <Toolbar>
            {/* Logo on the far left */}
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, ml: isMobile ? 0 : isTablet ? 2 : 8 }}>
              <img
                src='/icon.png'
                height='30px'
                alt="TimeCapsule Logo"
                style={{ marginRight: "15px" }}
              />
              <img
                src='/text-logo2.png'
                height='40px'
                alt="TimeCapsule Logo"
                style={{}}
              />

            </Typography>

            {/* Login Button on the far right */}
            {!user && (
              <Button
                variant="contained"
                size='small'
                startIcon={<LockOutlined />}
                onClick={login}
                sx={{
                  py: 1,
                  px: isMobile ? 2 : 3,
                  borderRadius: 2,
                  fontSize: isMobile ? '0.6rem' : '1rem',
                  background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                  '&:hover': {
                    background: "linear-gradient(135deg, #7B2CBF, #E31B88)",
                    transition: 'all 0.3s ease-in-out'
                  },
                }}
              >
                Log In
              </Button>
            )}
          </Toolbar>
        </AppBar>
      }
      <Container sx={{ mt: user ? 0 : 12, mb: 4 }}>
        <Typography variant="h4" gutterBottom align="left" sx={{ mt: 4, mb: 4, fontWeight: 'bold', color: 'white' }}>
          About Us
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph>
              TimeCapsule was born from a simple yet powerful idea: to create a digital space where memories can be preserved and shared across time. In our fast-paced digital world, we often lose sight of the moments that truly matter. Our founder, inspired by the discovery of an old family time capsule, set out to bring this concept into the digital age.
            </Typography>
            <Typography variant="body1" paragraph>
              What started as a personal project quickly grew into a platform that resonates with people from all walks of life. TimeCapsule allows users to capture not just photos and videos, but also thoughts, feelings, and aspirations, storing them securely until a predetermined future date.
            </Typography>

          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <img
                src="/about.jpg"
                alt="TimeCapsule Concept"
                style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Our Vision Section */}
        <Box
          sx={{
            my: 8,
            p: 4,
            borderRadius: 4,
            background: 'linear-gradient(45deg, #2D1B4E 30%, #7B2CBF 90%)',
            boxShadow: '0 4px 20px rgba(123, 44, 191, 0.15)'
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4, color: 'white', textAlign: 'center' }}>
            Our Vision
          </Typography>

          <Grid container spacing={isMobile ? 2 : 4}>
            {[
              { icon: <AccessTime sx={{ color: '#7B2CBF' }} />, title: "Preserve Moments", description: "Create a digital legacy that stands the test of time." },
              { icon: <Visibility sx={{ color: '#7B2CBF' }} />, title: "Rediscover Memories", description: "Experience the joy of revisiting forgotten moments." },
              { icon: <Group sx={{ color: '#7B2CBF' }} />, title: "Connect Generations", description: "Bridge the gap between past, present, and future." },
              { icon: <Lightbulb sx={{ color: '#7B2CBF' }} />, title: "Inspire Growth", description: "Reflect on personal journeys and celebrate progress." },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={3}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: 3,
                    bgcolor: 'rgba(30, 42, 56, 0.8)',
                    color: 'text.primary',
                    height: '100%',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: 'rgba(30, 42, 56, 0.95)',
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  <Box sx={{ 
                    mb: 2, 
                    fontSize: 40
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" component="h3" sx={{ color: '#E0E0E0', mb: 2 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#B0BEC5' }}>{item.description}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
            Looking to the Future
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: 'white' }}>
            As we continue to grow, our commitment to preserving memories remains steadfast. We're constantly exploring new ways to enhance the TimeCapsule experience...
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

// Wrapper component that adds SEO enhancements
const AboutPage: React.FC = () => {
  useEffect(() => {
    // Update document title for better SEO
    document.title = "About TimeCapsule - Our Story and Vision";

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content",
        "Learn about TimeCapsule - a digital space where memories can be preserved and shared across time. Discover our story, vision and the team behind the app.");
    }

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About TimeCapsule",
      "description": "TimeCapsule allows users to capture photos, videos, thoughts, feelings, and aspirations, storing them securely until a predetermined future date.",
      "publisher": {
        "@type": "Organization",
        "name": "TimeCapsule App",
        "logo": {
          "@type": "ImageObject",
          "url": "https://capsuletime.co/favicon-32x32.png"
        }
      },
      "mainEntity": {
        "@type": "WebSite",
        "name": "TimeCapsule",
        "url": "https://capsuletime.co"
      }
    };

    // Add the structured data script to the page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Clean up function to remove the script when component unmounts
    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return <AboutPageContent />;
};

export default AboutPage;
